import { getClient } from '../utils/getClient';
import { useQuery } from '@tanstack/react-query';

export const FEATURE_QUERY_KEY = ['RedVelvetApi', 'getFeatures'];

/**
 * get features from the server or session storage cache. Value will be null until a valid
 * server response has been returned.
 *
 * The user must be logged in for features to work so if you aren't logged in you should not bother
 * Loading these yet.
 *
 * In order for this to work somewhere in the application above you must have defined <ProvideFeatureContext  .../>
 */
export interface FeatureState {
  feature: string;
  value: boolean;
}

export function useFeatures(): FeatureState[] | null {
  const featureQuery = useQuery({
    queryKey: FEATURE_QUERY_KEY,
    // Get client is specifically called with an empty array of features to prevent dependency loop
    queryFn: async () => (await getClient([]).getFeatures({}, {})).data,
  });

  // If the data does not exist (because it is loading), we should return null to ensure that the
  // GateOnFeatureLoad component works properly
  return featureQuery.data || null;
}

export function getEnabledFeatures(features: FeatureState[] | null, searchParams: URLSearchParams) {
  const paramsFeatures = searchParams.get('features');
  if (paramsFeatures === 'default') return [];

  const urlFeatures = paramsFeatures?.split(',') || [];
  const apiFeatures =
    features
      ?.map((feature) => (feature.value ? feature.feature : ''))
      .filter((feature) => feature) || [];

  return urlFeatures.concat(apiFeatures);
}

export function featureIsEnabled(
  featureName: string,
  features: FeatureState[] | null,
  searchParams: URLSearchParams
) {
  const paramsFeatures = searchParams.get('features');
  if (searchParams.get('features') === 'default') return false;
  //return a coherant type of boolean.
  const lowerCaseFeatureName = featureName.toLowerCase();
  return (features &&
    features.some(
      (feature) => feature.feature.toLowerCase() === lowerCaseFeatureName && feature.value
    )) ||
    (paramsFeatures && paramsFeatures.toLowerCase().includes(lowerCaseFeatureName))
    ? true
    : false;
}
